import { z } from 'zod';
import {
  AssociationStatus,
  ClaimBasis,
  CompletionBasis,
  CompletionPolicy,
  ContributionType,
  CreationPolicy,
  DeliveryState,
  EvidenceBasis,
  EvidenceKind,
  MilestoneType,
  Priority,
  RelationKind,
  Visibility,
  WorkAction,
  WorkPhase,
  WorkType,
  AgentClient,
} from './enums.js';
import {
  ContentLocale,
  IsoDateTime,
  LongMarkdown,
  Reason,
  ShortText,
  SummaryMarkdown,
  Title,
  UserRef,
  Uuid,
  Version,
  WorkKey,
  boundedList,
} from './common.js';
import { AccessGrantInput } from './identity.js';

export const ProjectRef = z.object({ id: Uuid, key: z.string(), name: z.string() });
export type ProjectRef = z.infer<typeof ProjectRef>;
export const TeamRef = z.object({ id: Uuid, name: z.string(), slug: z.string() });
export type TeamRef = z.infer<typeof TeamRef>;

export const RepositoryRef = z.object({ id: Uuid, fullName: z.string(), url: z.string().nullable() });
export type RepositoryRef = z.infer<typeof RepositoryRef>;

// ---------------------------------------------------------------- Projects

export const ProjectView = z.object({
  id: Uuid,
  key: z.string(),
  name: z.string(),
  descriptionMarkdown: z.string(),
  visibility: Visibility,
  version: Version,
  owningTeams: z.array(TeamRef),
  managers: z.array(UserRef),
  defaultCompletionPolicy: CompletionPolicy,
  requiresReview: z.boolean(),
  repositories: z.array(RepositoryRef),
  archivedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  openWorkCount: z.number().int(),
  availableActions: z.array(z.string()),
});
export type ProjectView = z.infer<typeof ProjectView>;

export const CreateProject = z
  .object({
    key: z.string().regex(/^[A-Z][A-Z0-9]{1,9}$/),
    name: Title,
    descriptionMarkdown: LongMarkdown.optional(),
    owningTeamIds: boundedList(Uuid, 20).optional(),
    managerIds: boundedList(Uuid, 20).optional(),
    visibility: Visibility.optional(),
    grants: boundedList(AccessGrantInput).optional(),
    defaultCompletionPolicy: CompletionPolicy.optional(),
    requiresReview: z.boolean().optional(),
  })
  .strict();
export type CreateProject = z.infer<typeof CreateProject>;

export const UpdateProject = z
  .object({
    expectedVersion: Version,
    name: Title.optional(),
    descriptionMarkdown: LongMarkdown.optional(),
    owningTeamIds: boundedList(Uuid, 20).optional(),
    managerIds: boundedList(Uuid, 20).optional(),
    defaultCompletionPolicy: CompletionPolicy.optional(),
    requiresReview: z.boolean().optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateProject = z.infer<typeof UpdateProject>;

// ---------------------------------------------------------------- Work items

export const AcceptanceCriterionStatus = z.enum(['OPEN', 'MET', 'NOT_MET', 'WAIVED']);
export const AcceptanceCriterion = z.object({
  id: z.string().min(1).max(40),
  text: z.string().min(1).max(2000),
  status: AcceptanceCriterionStatus,
  note: z.string().max(2000).nullable(),
});
export type AcceptanceCriterion = z.infer<typeof AcceptanceCriterion>;

export const AcceptanceCriterionInput = z
  .object({ id: z.string().min(1).max(40).optional(), text: z.string().min(1).max(2000) })
  .strict();

export const Responsibility = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('team'), teamId: Uuid }).strict(),
  z.object({ kind: z.literal('personal'), personalOwnerId: Uuid }).strict(),
]);
export type Responsibility = z.infer<typeof Responsibility>;

export const BlockerView = z.object({
  id: Uuid,
  version: Version,
  reason: z.string(),
  preventsCompletion: z.boolean(),
  dependencyWorkItem: z.object({ id: Uuid, key: z.string(), title: z.string() }).nullable(),
  openedBy: UserRef.nullable(),
  openedAt: IsoDateTime,
  resolvedAt: IsoDateTime.nullable(),
  resolutionReason: z.string().nullable(),
});
export type BlockerView = z.infer<typeof BlockerView>;

export const WorkRelationView = z.object({
  id: Uuid,
  kind: RelationKind,
  direction: z.enum(['OUTGOING', 'INCOMING']),
  requiresCompletion: z.boolean(),
  other: z.object({ id: Uuid, key: z.string(), title: z.string(), phase: WorkPhase }),
});
export type WorkRelationView = z.infer<typeof WorkRelationView>;

export const ContributionView = z.object({
  user: UserRef,
  types: z.array(ContributionType),
  firstObservedAt: IsoDateTime,
  lastObservedAt: IsoDateTime,
});

export const WorkItemView = z.object({
  id: Uuid,
  key: WorkKey,
  aliases: z.array(z.string()),
  following: z.boolean(),
  version: Version,
  contentRevision: z.number().int(),
  title: z.string(),
  type: WorkType,
  descriptionMarkdown: z.string(),
  acceptanceCriteria: z.array(AcceptanceCriterion),
  phase: WorkPhase,
  deliveryState: DeliveryState,
  completionPolicy: CompletionPolicy,
  associationStatus: AssociationStatus,
  priority: Priority,
  visibility: Visibility,
  owner: UserRef.nullable(),
  assignee: UserRef.nullable(),
  reviewer: UserRef.nullable(),
  team: TeamRef.nullable(),
  project: ProjectRef.nullable(),
  labels: z.array(z.string()),
  originalLocale: z.string(),
  blocked: z.boolean(),
  blockers: z.array(BlockerView),
  relations: z.array(WorkRelationView),
  contributors: z.array(ContributionView),
  repositories: z.array(RepositoryRef),
  outcomeMarkdown: z.string().nullable(),
  completionBasis: CompletionBasis.nullable(),
  completedAt: IsoDateTime.nullable(),
  startedAt: IsoDateTime.nullable(),
  currentEvidenceBasis: EvidenceBasis.nullable(),
  deliveryScopeStatus: z.enum(['NONE', 'AWAITING_SCOPE', 'UNCONFIRMED', 'CONFIRMED']),
  linkedRequests: z.array(z.object({ id: Uuid, key: z.string(), title: z.string(), required: z.boolean() })),
  createdBy: UserRef.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** Archived work keeps its history but leaves the default lists; restore it to change it again. */
  archivedAt: IsoDateTime.nullable(),
  commentCount: z.number().int(),
  availableActions: z.array(z.string()),
});
export type WorkItemView = z.infer<typeof WorkItemView>;

export const WorkItemListItem = WorkItemView.pick({
  id: true,
  key: true,
  version: true,
  title: true,
  type: true,
  phase: true,
  deliveryState: true,
  completionPolicy: true,
  associationStatus: true,
  priority: true,
  visibility: true,
  assignee: true,
  owner: true,
  team: true,
  project: true,
  labels: true,
  blocked: true,
  originalLocale: true,
  updatedAt: true,
  createdAt: true,
  archivedAt: true,
  commentCount: true,
});
export type WorkItemListItem = z.infer<typeof WorkItemListItem>;

export const CreateWorkItem = z
  .object({
    title: Title,
    type: WorkType,
    completionPolicy: CompletionPolicy.optional(),
    responsibility: Responsibility,
    originalLocale: ContentLocale,
    projectId: Uuid.optional(),
    descriptionMarkdown: LongMarkdown.optional(),
    acceptanceCriteria: boundedList(AcceptanceCriterionInput).optional(),
    assigneeId: Uuid.optional(),
    priority: Priority.optional(),
    visibility: Visibility.optional(),
    grants: boundedList(AccessGrantInput).optional(),
    repositoryIds: boundedList(Uuid).optional(),
    labels: boundedList(z.string().min(1).max(40), 30).optional(),
    parentId: Uuid.optional(),
    workIntentId: z.string().min(8).max(100).optional(),
    requestId: Uuid.optional(),
  })
  .strict();
export type CreateWorkItem = z.infer<typeof CreateWorkItem>;

export const UpdateWorkItem = z
  .object({
    expectedVersion: Version,
    title: Title.optional(),
    descriptionMarkdown: LongMarkdown.optional(),
    acceptanceCriteria: boundedList(AcceptanceCriterion).optional(),
    priority: Priority.optional(),
    assigneeId: Uuid.nullable().optional(),
    reviewerId: Uuid.nullable().optional(),
    type: WorkType.optional(),
    labels: boundedList(z.string().min(1).max(40), 30).optional(),
    projectId: Uuid.nullable().optional(),
    responsibility: Responsibility.optional(),
    completionPolicy: CompletionPolicy.optional(),
    policyChangeReason: Reason.optional(),
    originalLocale: ContentLocale.optional(),
    /** Replaces the linked repositories. Linking never rewrites earlier completion episodes. */
    repositoryIds: boundedList(Uuid).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 1, { message: 'at least one editable field is required' });
export type UpdateWorkItem = z.infer<typeof UpdateWorkItem>;

export const DeliverableInput = z
  .object({
    outcomeMarkdown: LongMarkdown.refine((v) => v.trim().length > 0, { message: 'outcome required' }),
    criteria: boundedList(
      z.object({ id: z.string(), status: z.enum(['MET', 'NOT_MET', 'WAIVED']), note: z.string().max(2000).optional() }).strict(),
    ).optional(),
    evidenceIds: boundedList(Uuid).optional(),
  })
  .strict();
export type DeliverableInput = z.infer<typeof DeliverableInput>;

export const TransitionWork = z
  .object({
    expectedVersion: Version,
    action: WorkAction,
    reason: Reason.optional(),
    deliverable: DeliverableInput.optional(),
    reviewerId: Uuid.optional(),
    evidenceIds: boundedList(Uuid).optional(),
  })
  .strict();
export type TransitionWork = z.infer<typeof TransitionWork>;

/** Review of a provisional agent-created item (Section 7.3): confirm it, or merge it into existing work. */
export const ReviewAssociation = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('CONFIRM'), expectedVersion: Version }).strict(),
  z.object({ decision: z.literal('MERGE'), expectedVersion: Version, targetWorkItemId: Uuid, reason: Reason }).strict(),
]);
export type ReviewAssociation = z.infer<typeof ReviewAssociation>;

export const SetBlocker = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('OPEN'),
      reason: Reason,
      preventsCompletion: z.boolean(),
      dependencyWorkItemId: Uuid.optional(),
    })
    .strict(),
  z.object({ action: z.literal('RESOLVE'), blockerId: Uuid, expectedVersion: Version, reason: Reason }).strict(),
]);
export type SetBlocker = z.infer<typeof SetBlocker>;

export const AddWorkRelation = z
  .object({
    kind: RelationKind,
    targetWorkItemId: Uuid,
    requiresCompletion: z.boolean().optional(),
  })
  .strict();
export type AddWorkRelation = z.infer<typeof AddWorkRelation>;

export const BulkUpdateWork = z
  .object({
    items: boundedList(z.object({ id: Uuid, expectedVersion: Version }).strict()),
    priority: Priority.optional(),
    assigneeId: Uuid.nullable().optional(),
    labelsAdd: boundedList(z.string().min(1).max(40), 30).optional(),
    labelsRemove: boundedList(z.string().min(1).max(40), 30).optional(),
    /** Moves every item into this project (null removes the project). */
    projectId: Uuid.nullable().optional(),
    /** Makes this team responsible for every item. */
    teamId: Uuid.optional(),
    /** Archives (only completed or canceled work) or restores every item. */
    archived: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).some((k) => k !== 'items'), { message: 'at least one change is required' });
export type BulkUpdateWork = z.infer<typeof BulkUpdateWork>;

/** Archive or restore one work item. Only completed or canceled work can be archived. */
export const ArchiveWorkItem = z.object({ expectedVersion: Version, archived: z.boolean() }).strict();
export type ArchiveWorkItem = z.infer<typeof ArchiveWorkItem>;

/** Administrator-only permanent deletion; the reason stays in the audit log. */
export const DeleteWorkItem = z.object({ expectedVersion: Version, reason: Reason }).strict();
export type DeleteWorkItem = z.infer<typeof DeleteWorkItem>;

export const WorkLabelCount = z.object({ label: z.string(), count: z.number().int() });
export type WorkLabelCount = z.infer<typeof WorkLabelCount>;

export const WorkListQuery = z.object({
  q: z.string().max(200).optional(),
  phase: z.string().max(200).optional(),
  type: z.string().max(200).optional(),
  priority: z.string().max(100).optional(),
  projectId: Uuid.optional(),
  teamId: Uuid.optional(),
  assigneeId: z.union([Uuid, z.literal('me'), z.literal('none')]).optional(),
  associationStatus: AssociationStatus.optional(),
  following: z.enum(['true']).optional(),
  blocked: z.enum(['true', 'false']).optional(),
  includeCanceled: z.enum(['true', 'false']).optional(),
  /** true lists only archived work; archived work is hidden otherwise. */
  archived: z.enum(['true']).optional(),
  label: z.string().max(40).optional(),
  sort: z.enum(['updatedAt', 'createdAt', 'priority', 'key']).optional(),
  cursor: z.string().max(512).optional(),
  limit: z.string().regex(/^\d{1,3}$/).optional(),
});
export type WorkListQuery = z.infer<typeof WorkListQuery>;

/** Spreadsheet download of a filtered list (same filters and access as the list). */
export const ListExportFormat = z.enum(['csv', 'xlsx']);
export type ListExportFormat = z.infer<typeof ListExportFormat>;
export const LIST_EXPORT_MAX_ROWS = 5000;

// ---------------------------------------------------------------- Agent capture

export const ResolveWork = z
  .object({
    objective: SummaryMarkdown,
    explicitKey: z.string().max(40).optional(),
    sessionId: Uuid.optional(),
    projectId: Uuid.optional(),
    repositoryIds: boundedList(Uuid).optional(),
    repositoryFullName: z.string().max(200).optional(),
    branchRef: z.string().max(255).optional(),
    pullRequestId: Uuid.optional(),
  })
  .strict();
export type ResolveWork = z.infer<typeof ResolveWork>;

export const WorkCandidate = z.object({ id: Uuid, key: z.string(), title: z.string(), reason: z.string() });
export type WorkCandidate = z.infer<typeof WorkCandidate>;

export const ResolveWorkResult = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('linked'), workItemId: Uuid, key: z.string(), basis: z.string() }),
  z.object({ outcome: z.literal('needs_selection'), workItemId: z.null(), candidates: z.array(WorkCandidate) }),
  z.object({ outcome: z.literal('not_found'), workItemId: z.null(), reason: z.string().optional() }),
]);
export type ResolveWorkResult = z.infer<typeof ResolveWorkResult>;

export const ProposedWork = z
  .object({
    title: Title,
    type: WorkType,
    completionPolicy: CompletionPolicy.optional(),
    descriptionMarkdown: LongMarkdown.optional(),
    acceptanceCriteria: boundedList(AcceptanceCriterionInput).optional(),
    responsibility: Responsibility.optional(),
    projectId: Uuid.optional(),
    priority: Priority.optional(),
    originalLocale: ContentLocale.optional(),
  })
  .strict();
export type ProposedWork = z.infer<typeof ProposedWork>;

export const EnsureWorkItem = ResolveWork.extend({
  workIntentId: z.string().min(8).max(100),
  creationPolicy: CreationPolicy,
  proposedWork: ProposedWork.optional(),
}).strict();
export type EnsureWorkItem = z.infer<typeof EnsureWorkItem>;

export const EnsureWorkResult = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('linked'), workItemId: Uuid, key: z.string(), basis: z.string() }),
  z.object({ outcome: z.literal('created'), workItemId: Uuid, key: z.string(), associationStatus: AssociationStatus }),
  z.object({ outcome: z.literal('needs_selection'), workItemId: z.null(), candidates: z.array(WorkCandidate) }),
  z.object({ outcome: z.literal('not_found'), workItemId: z.null(), reason: z.string().optional() }),
]);
export type EnsureWorkResult = z.infer<typeof EnsureWorkResult>;

export const StartAgentSession = z
  .object({
    client: AgentClient,
    clientSessionRef: z.string().min(1).max(200),
    workItemIds: boundedList(Uuid, 20).optional(),
    repositoryFullName: z.string().max(200).optional(),
    branchRef: z.string().max(255).optional(),
    objective: SummaryMarkdown.optional(),
  })
  .strict();
export type StartAgentSession = z.infer<typeof StartAgentSession>;

export const AgentSessionView = z.object({
  id: Uuid,
  version: Version,
  client: AgentClient,
  clientSessionRef: z.string(),
  initiatingUser: UserRef,
  status: z.enum(['ACTIVE', 'FINISHED']),
  workItems: z.array(z.object({ id: Uuid, key: z.string(), title: z.string() })),
  branchRef: z.string().nullable(),
  repositoryFullName: z.string().nullable(),
  startedAt: IsoDateTime,
  endedAt: IsoDateTime.nullable(),
  outcome: z.string().nullable(),
  handoffSummary: z.string().nullable(),
});
export type AgentSessionView = z.infer<typeof AgentSessionView>;

export const StructuredCheck = z
  .object({
    name: z.string().min(1).max(200),
    result: z.enum(['PASSED', 'FAILED', 'SKIPPED', 'NOT_RUN']),
    claimBasis: ClaimBasis,
    detail: z.string().max(2000).optional(),
  })
  .strict();
export type StructuredCheck = z.infer<typeof StructuredCheck>;

export const RecordProgress = z
  .object({
    workItemId: Uuid,
    milestoneType: MilestoneType,
    summaryMarkdown: SummaryMarkdown,
    evidenceIds: boundedList(Uuid).optional(),
    checks: boundedList(StructuredCheck, 50).optional(),
  })
  .strict();
export type RecordProgress = z.infer<typeof RecordProgress>;

export const FinishAgentSession = z
  .object({
    expectedVersion: Version,
    outcome: z.enum(['COMPLETED_STEP', 'HANDOFF', 'ABANDONED', 'BLOCKED']),
    handoffSummary: SummaryMarkdown.optional(),
  })
  .strict();
export type FinishAgentSession = z.infer<typeof FinishAgentSession>;

export const ProgressEntryView = z.object({
  id: Uuid,
  milestoneType: MilestoneType,
  summaryMarkdown: z.string(),
  checks: z.array(StructuredCheck),
  actor: UserRef.nullable(),
  client: AgentClient.nullable(),
  sessionId: Uuid.nullable(),
  occurredAt: IsoDateTime,
});
export type ProgressEntryView = z.infer<typeof ProgressEntryView>;

// ---------------------------------------------------------------- Evidence

export const AttachEvidence = z
  .object({
    parentKind: z.enum(['WORK_ITEM', 'REQUEST']),
    parentId: Uuid,
    kind: EvidenceKind,
    title: Title,
    url: z.url().max(2000).optional(),
    summaryMarkdown: z.string().max(8192).optional(),
    externalRef: z.string().max(200).optional(),
  })
  .strict();
export type AttachEvidence = z.infer<typeof AttachEvidence>;

export const EvidenceView = z.object({
  id: Uuid,
  kind: EvidenceKind,
  basis: EvidenceBasis,
  title: z.string(),
  url: z.string().nullable(),
  summaryMarkdown: z.string().nullable(),
  externalRef: z.string().nullable(),
  recordedBy: UserRef.nullable(),
  recordedAt: IsoDateTime,
});
export type EvidenceView = z.infer<typeof EvidenceView>;

export const TimelineEntry = z.object({
  id: Uuid,
  occurredAt: IsoDateTime,
  type: z.string(),
  actor: UserRef.nullable(),
  actorKind: z.string(),
  source: z.string(),
  summary: z.record(z.string(), z.unknown()),
});
export type TimelineEntry = z.infer<typeof TimelineEntry>;

export const CompletionEpisodeView = z.object({
  id: Uuid,
  episode: z.number().int(),
  startedAt: IsoDateTime.nullable(),
  completedAt: IsoDateTime.nullable(),
  reopenedAt: IsoDateTime.nullable(),
  completionBasis: CompletionBasis.nullable(),
  reopenReason: z.string().nullable(),
});
export type CompletionEpisodeView = z.infer<typeof CompletionEpisodeView>;

export { ShortText };
